import {
  BoxSection,
  ButtonDefault,
  ButtonPrimary,
  Heading3,
} from 'app/components';
import { TProducts, useProductsContext } from 'modules/products/context';
import { EBeneficiaryType } from 'modules/products/context/ProductsProvider/products.interfaces';
import { useProductsService } from 'modules/products/services/hooks';
import { useCallback, useEffect, useState, useContext } from 'react';
import { ApplicationRequestContext } from '../ApplicationRequest';
import { ListBox, ListBoxItem, ListBoxItemTitle } from '../components';
import { StepContentButtonsArea } from '../styles';
import { ProductStepContent } from './styles';
import { useLocation } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { useAppContext } from 'app/context/AppContextProvider';
import { useSnackbar } from 'app/hooks/useSnackbar';

const MULTI_SPLIT_APPLICATION_MESSAGE =
  'A solicitação deste tipo de produto só pode ser criada via API.';

const ProductStep = () => {
  const location = useLocation();
  const {
    product,
    borrowerData,
    hasMultiplesFundings,
    onBack,
    setProduct,
    onForward,
    clearProductSelection,
  } = useContext(ApplicationRequestContext);
  const [productList, setProductList] = useState<TProducts[]>([]);
  const { getProducts } = useProductsService();
  const { is_issuer_app } = useAppContext();
  const { showSnackbar } = useSnackbar();
  const {
    state: { content, has_next },
  } = useProductsContext();

  const isMultiSplitProduct = useCallback(
    (selectedProduct?: TProducts | null) =>
      selectedProduct?.beneficiary_type === EBeneficiaryType.MULTISPLIT,
    [],
  );

  const handleSelectProduct = useCallback(
    (item: TProducts) => {
      clearProductSelection();
      setProduct(item);

      if (isMultiSplitProduct(item)) {
        showSnackbar(MULTI_SPLIT_APPLICATION_MESSAGE, 'info');
      }
    },
    [clearProductSelection, setProduct, isMultiSplitProduct, showSnackbar],
  );

  const onSearch = useCallback(
    (field: string, value: string) => {
      if (value === '') {
        setProductList(content);
        return;
      }

      const filteredProducts = content.filter((item: TProducts) => {
        if (
          item[field as keyof TProducts]
            ?.toString()
            ?.toLowerCase()
            ?.includes(value?.toLocaleLowerCase())
        ) {
          return item;
        }
      });

      setProductList(filteredProducts);
    },
    [content],
  );

  const handleGetProducts = useCallback(async () => {
    await getProducts();
  }, []);

  useEffect(() => {
    handleGetProducts();
  }, []);

  useEffect(() => {
    if (content) {
      setProductList(content);
      if (typeof location.state?.product === 'object') {
        const selectedProduct = location.state.product as TProducts;
        setProduct(selectedProduct);
        history.replaceState({}, document.title);

        if (isMultiSplitProduct(selectedProduct)) {
          showSnackbar(MULTI_SPLIT_APPLICATION_MESSAGE, 'info');
        }
      }
    }
  }, [content]);

  return (
    <BoxSection>
      <ProductStepContent>
        <Heading3>
          Qual o <strong>produto </strong>
          dessa solicitação?
        </Heading3>
        <ListBox
          filterItems={[
            { name: 'Nome', parameterName: 'name' },
            { name: 'Descrição', parameterName: 'description' },
          ]}
          hasMoreItems={has_next}
          items={productList?.filter(
            (productListItem) =>
              !borrowerData ||
              (productListItem.borrower_type === 'PERSON' &&
                borrowerData.taxpayer_id.length === 11) ||
              (productListItem.borrower_type === 'BUSINESS' &&
                borrowerData.taxpayer_id.length > 11),
          )}
          onSearch={onSearch}
        >
          {(i) => {
            const item = i as TProducts;
            return (
              <ListBoxItem
                className={product?.name === item.name ? 'selected' : ''}
                onClick={() => handleSelectProduct(item)}
                key={item.name}
              >
                <ListBoxItemTitle>{item.name}</ListBoxItemTitle>
                <span>{item.description}</span>
              </ListBoxItem>
            );
          }}
        </ListBox>
        <StepContentButtonsArea>
          {(hasMultiplesFundings || is_issuer_app) && (
            <ButtonDefault margin={true} onClick={() => onBack()}>
              <ArrowBack />
            </ButtonDefault>
          )}
          <ButtonPrimary
            disabled={!product || isMultiSplitProduct(product)}
            onClick={() => {
              if (product && !isMultiSplitProduct(product)) {
                setProduct(product);
                setTimeout(() => onForward());
              }
            }}
          >
            Avançar
          </ButtonPrimary>
        </StepContentButtonsArea>
      </ProductStepContent>
    </BoxSection>
  );
};

export default ProductStep;
